import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "@/components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  ChevronRight,
  Clock,
  GraduationCap,
  Info,
  MessageCircle,
  Phone,
  Minus,
  Plus,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/scar-management-treatment-mumbai";
const RECONSTRUCTIVE_PATH = "/reconstructive-surgery-in-mumbai";
const KELOID_PATH = "/keloid-treatment-mumbai";
const BURNS_PATH = "/burns-burn-scar-treatment-mumbai";
const CHEMICAL_PEELS_PATH = "/chemical-peels-mumbai";

const linkClass = "text-gold hover:underline font-medium";

const SCAR_FAQS = [
  {
    question: "Can scars be completely removed?",
    answer:
      "No. Scars represent a permanent structural change in the skin that cannot be completely reversed to normal skin by any available treatment. What scar management achieves is significant reduction in visibility, improvement in texture and color, and relief of symptoms. A well-treated scar is less visible, flatter, softer, and more natural in appearance than an untreated scar, but it remains distinguishable from normal skin on close examination. The goal is a scar that is no longer noticeable in normal daily life, not a scar that is invisible.",
  },
  {
    question: "How many treatment sessions are needed?",
    answer:
      "This depends on the scar type, size, and the treatments being used. Hypertrophic scars managed with steroid injections typically need 4 to 8 sessions over 6 to 12 months. Acne scars managed with microneedling and laser typically need 6 to 10 sessions over 12 to 18 months. Keloids require ongoing maintenance after the initial treatment course. Each scar is assessed individually and the expected number of sessions is discussed during consultation based on the specific scar being treated.",
  },
  {
    question: "When is the best time to start scar treatment?",
    answer:
      "As early as possible after the wound has healed. Silicone therapy should start immediately when the wound is closed. Steroid injections for hypertrophic or keloid scars are most effective when started early in the active scar phase. Laser and microneedling for acne scars can begin from 6 to 8 weeks after the acne lesion has healed. The active phase of scar formation, the first 12 to 18 months after injury, is when the scar tissue is most responsive to treatment. Starting early reduces the eventual burden of treatment needed.",
  },
  {
    question: "What is the best treatment for acne scars?",
    answer:
      "There is no single best treatment because acne scars are a heterogeneous group. Icepick scars respond to punch techniques and fractional laser. Rolling scars respond to subcision and dermal fillers. Boxcar scars respond to fractional laser and microneedling. Post-inflammatory pigmentation responds to chemical peels and laser. Most patients with acne scarring have a combination of these scar subtypes, requiring a combination treatment plan that addresses each subtype with the most effective modality. An assessment that identifies which types are present is the essential first step.",
  },
  {
    question: "Do scar creams work?",
    answer:
      "Silicone-based scar gels and sheets have good evidence supporting their effectiveness for hypertrophic scar prevention and management when used consistently for 6 months or more. Other scar creams, including most over-the-counter products, have limited evidence for significant scar improvement beyond basic moisturization. No cream can remove an established scar, fill a depressed scar, or flatten a keloid. Topical treatments are most valuable as part of a comprehensive plan that includes other modalities, or as preventive therapy for new scars.",
  },
  {
    question: "Does skin type affect scar treatment?",
    answer:
      "Yes, significantly. Darker skin types, including most Indian patients, are more prone to post-inflammatory hyperpigmentation after skin injury or treatment. This means that aggressive treatments including high-concentration chemical peels, high-energy laser settings, and any treatment that creates significant skin injury must be used with greater caution and adapted protocols in darker skin to avoid worsening the pigmentation problem alongside the scar. Experienced scar management in darker skin types requires specific expertise in adapting treatments to the skin type.",
  },
  {
    question: "Can scar treatment help with burn scars?",
    answer:
      "Yes. Burn scars respond to silicone therapy, compression, laser therapy, fat grafting, and in cases of contracture, surgical release. Burn scar management is among the most complex scar work because of the large areas involved, the mixed scar types present, the functional restriction from contractures, and the pigmentation changes affecting healed burn skin. Meaningful improvement in burn scar appearance and function is achievable with comprehensive treatment, though it requires patience and a long-term plan.",
  },
  {
    question: "Is scar revision surgery always necessary?",
    answer:
      "No. Many scars respond well to non-surgical management without ever requiring surgery. Scar revision surgery is reserved for scars that have not responded adequately to non-surgical treatment, scars with poor orientation or excessive width that are structurally unlikely to improve without revision, and contracture scars causing functional restriction that requires release. The majority of patients presenting with cosmetic scar concerns are managed with a combination of non-surgical modalities without surgical revision.",
  },
];

const treatmentModalities = [
  "Silicone gel and sheeting",
  "Steroid injections",
  "Laser therapy",
  "Microneedling",
  "Subcision",
  "Dermal fillers",
  "Chemical peels",
  "PRP therapy",
  "Fat grafting",
  "Scar revision surgery",
];

const possibleTreatmentSchema = treatmentModalities.map((name) => ({
  "@type": "MedicalTherapy",
  name,
}));

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalCondition",
      name: "Scar",
      description:
        "Cutaneous scarring after injury, surgery, acne, or burns — managed with combined non-surgical and surgical modalities to improve appearance, texture, symptoms, and function.",
      possibleTreatment: possibleTreatmentSchema,
      url: PAGE_URL,
    },
    {
      "@type": "MedicalProcedure",
      name: "Scar management",
      description:
        "Individualized assessment and sequenced treatment combining silicone therapy, injections, energy-based devices, microneedling, fillers, peels, PRP, fat grafting, and scar revision surgery as indicated.",
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: SCAR_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function ScarManagementJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Reconstructive Surgery", href: RECONSTRUCTIVE_PATH },
    { name: "Scar Management", href: "/scar-management-treatment-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Scar Management</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-4">Scar Management Treatment</h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg sm:text-xl text-deep-navy/65 leading-relaxed mb-7 max-w-xl font-light">
              Advanced Scar Reduction &amp; Skin Restoration by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-2">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-semibold text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Scar Management" icon="🩹" size="hero"  imageSrc={HERO.scarManagement} imageAlt="Scar management"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">What is Scar Management?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Scar management is the clinical discipline of reducing scar visibility, improving skin texture and tone, and relieving scar-related symptoms such as itching, tightness, and pain using a combination of non-surgical and surgical treatments tailored to the specific scar type, age, and location. No single treatment addresses all types of scars. Effective scar management requires accurate assessment of the scar&apos;s characteristics before selecting the appropriate treatment or combination of treatments.
            </p>
            <p>
              The foundation of scar management is an understanding of how scars form. When the skin heals after injury, the body deposits collagen rapidly to close the wound. This repair collagen differs from the organized structure of normal skin collagen in its arrangement, texture, and pigmentation. The visible difference between the scar and surrounding normal skin is the result of this structural difference. Treatment works by modifying this abnormal collagen through controlled stimulation, remodeling, or surgical replacement with better-organized tissue.
            </p>
            <p>
              Dr. Vicky Jain provides comprehensive scar management for acne scars, surgical scars,{" "}
              <Link to={BURNS_PATH} className={linkClass}>
                burn scars
              </Link>
              , traumatic scars,{" "}
              <Link to={KELOID_PATH} className={linkClass}>
                keloids
              </Link>
              , and hypertrophic scars. Treatment plans are designed individually based on detailed scar assessment and combine multiple modalities in the sequence most likely to produce the best cumulative improvement for each patient&apos;s specific scar type.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function EarlyExpectationsAlert() {
  return (
    <section className="bg-warm-white pb-10 md:pb-14" data-testid="expectations-alert-section" aria-labelledby="expectations-alert-heading">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <Alert className="rounded-none border-2 border-gold/50 bg-white max-w-xl py-6 px-4 md:px-6 ml-auto">
          <Info className="h-5 w-5 text-gold" aria-hidden />
          <AlertTitle id="expectations-alert-heading" className="font-serif text-lg text-premium-purple pl-7 mb-2">
            What improvement is realistic
          </AlertTitle>
          <AlertDescription className="pl-7 text-deep-navy/80 leading-relaxed">
            Scars cannot be completely eliminated; they represent a permanent change in tissue architecture. With the right plan, they can often be{" "}
            <strong className="font-medium text-deep-navy">significantly improved</strong> in visibility, texture, color, and comfort. Honest goal-setting from the first visit is central to scar management.
          </AlertDescription>
        </Alert>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Our Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Scar Management</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                The first step in any scar management consultation is accurate classification of the scar. The treatment for a depressed atrophic acne scar is fundamentally different from the treatment for a raised hypertrophic surgical scar, which is different again from the treatment for a contracture scar after a burn. Applying the wrong treatment to a scar, or applying the right treatment at the wrong stage of scar maturation, produces poor results. This is why self-treating with creams, or receiving generic treatment without proper scar assessment, is ineffective for most significant scars.
              </p>
              <p>
                Dr. Jain approaches scar management as a long-term plan rather than a single intervention. Most meaningful scar improvement requires multiple treatment sessions over 6 to 18 months. The sequence of treatments matters: some modalities prepare the scar for subsequent treatments, some are most effective during the active scar phase, and some are only appropriate after the scar has fully matured. The plan is adjusted based on how the scar responds at each stage. Patients who commit to the full treatment course achieve significantly better results than those who stop after the first one or two sessions.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const SCAR_TYPE_CARDS = [
  {
    title: "Atrophic Scars",
    copy: "Depressed scars that sit below the surrounding skin surface because tissue was lost during healing. The most common cause is acne, particularly cystic acne that destroys the dermis. Atrophic acne scars are classified as icepick, rolling, or boxcar depending on their shape, which determines the specific treatment approach for each.",
  },
  {
    title: "Hypertrophic Scars",
    copy: "Raised scars that remain within the original wound boundary. They form when excess collagen is produced during healing but the process is self-limiting. Most hypertrophic scars improve significantly over 12 to 18 months without treatment, though this process is accelerated by silicone therapy, compression, and steroid injections for active, symptomatic scars.",
  },
  {
    title: "Keloid Scars",
    copy: (
      <>
        Raised scars that grow beyond the original wound boundary and do not resolve spontaneously.{" "}
        <Link to={KELOID_PATH} className={linkClass}>
          Keloids
        </Link>{" "}
        require combination treatment including steroid injections, silicone therapy, and in selected cases surgical excision with adjuvant treatment. Recurrence after treatment is common and long-term follow-up is required.
      </>
    ),
  },
  {
    title: "Contracture Scars",
    copy: (
      <>
        Scar tissue that crosses a joint or skin fold and tightens during healing, restricting normal movement. Most common after{" "}
        <Link to={BURNS_PATH} className={linkClass}>
          burns
        </Link>{" "}
        affecting joint surfaces. The functional restriction can be significant. Treatment requires contracture release surgery followed by physiotherapy and post-operative splinting.
      </>
    ),
  },
  {
    title: "Acne Scars",
    copy: "A specific and heterogeneous category of atrophic, hypertrophic, and pigmented scars resulting from inflammatory acne. The mix of scar types present in a single patient, and the co-existing active acne and pigmentation, require a carefully sequenced combination treatment plan addressing each scar type with the most appropriate modality.",
  },
  {
    title: "Pigmented Scars",
    copy: "Post-inflammatory hyperpigmentation or hypopigmentation following skin injury, acne, burns, or procedures. Darker skin types are more prone to post-inflammatory hyperpigmentation, which can persist for months to years. Chemical peels, laser, and depigmenting agents address pigmented scars, with treatment adapted to the patient&apos;s skin type.",
  },
];

function TypesOfScarsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="types-of-scars-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Types of Scars and Their Characteristics</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {SCAR_TYPE_CARDS.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const TREATMENT_OPTION_BLOCKS = [
  {
    title: "Silicone Gel and Sheeting",
    body: (
      <>
        The first-line treatment for hypertrophic and{" "}
        <Link to={KELOID_PATH} className={linkClass}>
          keloid
        </Link>{" "}
        scars and for prophylaxis of new scars in healing wounds. Applied continuously to the scar surface, silicone hydrates the stratum corneum, reduces tension on the wound edges, and directly inhibits excess collagen production. Most effective when started as soon as the wound has closed and continued for at least 6 months. Requires consistent use for meaningful benefit.
      </>
    ),
  },
  {
    title: "Steroid Injections",
    body: (
      <>
        Intralesional triamcinolone injections reduce the inflammatory activity and collagen overproduction in active hypertrophic and{" "}
        <Link to={KELOID_PATH} className={linkClass}>
          keloid
        </Link>{" "}
        scars. Multiple sessions spaced 4 to 6 weeks apart produce progressive flattening and symptom relief. Most effective for raised, itchy, actively growing scars. Combined with silicone therapy for synergistic effect.
      </>
    ),
  },
  {
    title: "Laser Therapy",
    body: (
      <>
        Multiple laser types address different scar characteristics. Pulsed dye laser reduces scar redness and vascular activity. Ablative fractional laser remodels scar texture and collagen architecture through controlled micro-injuries. Non-ablative fractional laser improves pigmentation and skin quality with less downtime. Laser is one of the most versatile scar treatments and is effective across multiple scar types including acne scars, surgical scars, and{" "}
        <Link to={BURNS_PATH} className={linkClass}>
          burn scars
        </Link>
        .
      </>
    ),
  },
  {
    title: "Microneedling",
    body: "Fine needles create controlled micro-injuries in the scar tissue that trigger collagen production without removing skin. Particularly effective for atrophic acne scars where the goal is to stimulate new collagen to fill the depressed area. Multiple sessions are required. Often combined with PRP applied during or after the microneedling session to enhance the growth factor stimulus.",
  },
  {
    title: "Subcision",
    body: "A needle is inserted beneath a depressed scar and moved laterally to cut the fibrous bands tethering the scar base to the underlying tissue. Releasing these bands allows the depressed scar to rise. Most effective for rolling acne scars and depressed scars with visible tethering. Often combined with dermal filler injected into the released space to maintain the improvement by filling the volume deficit.",
  },
  {
    title: "Dermal Fillers",
    body: "Hyaluronic acid filler injected beneath a depressed scar provides immediate volume restoration and surface smoothing. Particularly effective for rolling acne scars and depressed surgical scars. Results are temporary, lasting 6 to 12 months, but the repeated stimulation of collagen during filler integration provides some lasting improvement alongside the volumetric effect.",
  },
  {
    title: "Chemical Peels",
    titleIsLink: true,
    body: (
      <>
        Address surface pigmentation, skin texture, and superficial scar quality. Most appropriate for macular scars, pigmented post-acne marks, and as a preparatory or maintenance treatment between other scar management sessions. Peel depth and agent are matched to the scar type and patient skin type, with particular attention to post-inflammatory hyperpigmentation risk in darker skin tones. See{" "}
        <Link to={CHEMICAL_PEELS_PATH} className={linkClass}>
          chemical peels
        </Link>{" "}
        for how peels fit into broader skin renewal plans.
      </>
    ),
  },
  {
    title: "PRP Therapy",
    body: "Platelet-rich plasma applied to the scar during or after microneedling, laser, or other treatment sessions provides a concentrated growth factor stimulus that accelerates healing, enhances collagen induction, and improves overall treatment outcomes. Used as an adjunct to enhance the effect of primary treatments rather than as a standalone scar treatment.",
  },
  {
    title: "Fat Grafting",
    body: (
      <>
        Autologous fat injected beneath the scar improves volume, skin quality, and tissue pliability. The growth factors released by grafted fat cells have a regenerative effect on the damaged tissue. Most appropriate for depressed{" "}
        <Link to={BURNS_PATH} className={linkClass}>
          burn scars
        </Link>
        , atrophic scars over large areas, and scars where the underlying tissue quality needs improvement alongside surface treatment.
      </>
    ),
  },
  {
    title: "Scar Revision Surgery",
    body: "For scars with poor orientation, excessive width, distortion of surrounding anatomy, or contractures causing functional restriction. Techniques include excision and re-closure along better-oriented lines, Z-plasty to redirect scar tension, W-plasty to break up a linear scar, and contracture release with graft or flap coverage. Surgical revision addresses the structural problem that non-surgical treatments cannot resolve.",
  },
];

function TreatmentOptionsSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="treatment-options-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Scar Management Treatment Options</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {TREATMENT_OPTION_BLOCKS.map((block) => (
            <div key={block.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">
                {block.titleIsLink ? (
                  <Link to={CHEMICAL_PEELS_PATH} className="hover:text-gold transition-colors">
                    {block.title}
                  </Link>
                ) : (
                  block.title
                )}
              </h3>
              <p className="text-deep-navy/70 leading-relaxed">{block.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const BY_SCAR_TYPE_ROWS = [
  {
    type: "Acne Scars (Atrophic)",
    rec: "Microneedling, subcision, fractional laser, dermal fillers, chemical peels, PRP. Treatment combination depends on scar subtypes present.",
  },
  {
    type: "Hypertrophic Scars",
    rec: "Silicone therapy, steroid injections, pulsed dye laser. Surgery if non-surgical management fails.",
  },
  {
    type: "Keloid Scars",
    rec: (
      <>
        Combination steroid injections with silicone therapy, surgical excision combined with immediate steroid injection for large{" "}
        <Link to={KELOID_PATH} className={linkClass}>
          keloids
        </Link>
        , long-term maintenance.
      </>
    ),
  },
  {
    type: "Burn Scars",
    rec: (
      <>
        Silicone therapy, compression, laser, fat grafting, contracture release surgery for functional restriction. See{" "}
        <Link to={BURNS_PATH} className={linkClass}>
          burns &amp; burn scar treatment
        </Link>{" "}
        for comprehensive burn scar care.
      </>
    ),
  },
  {
    type: "Surgical Scars",
    rec: "Silicone therapy early, fractional laser after maturation, scar revision for poor cosmetic outcome.",
  },
  {
    type: "Post-Inflammatory Pigmentation",
    rec: (
      <>
        <Link to={CHEMICAL_PEELS_PATH} className={linkClass}>
          Chemical peels
        </Link>{" "}
        with appropriate skin preparation, fractional laser, depigmenting agents. Strict sun protection throughout.
      </>
    ),
  },
];

function TreatmentByScarTypeSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="treatment-by-scar-type-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">The Procedure</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Recommended Treatments by Scar Type</h2>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm md:text-base">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-4 pr-4 font-serif text-premium-purple font-normal w-[28%]">
                  Scar type
                </th>
                <th scope="col" className="py-4 pl-2 font-serif text-premium-purple">
                  Recommended approach
                </th>
              </tr>
            </thead>
            <tbody>
              {BY_SCAR_TYPE_ROWS.map((row) => (
                <tr key={row.type} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-4 pr-4 font-medium text-premium-purple align-top">
                    {row.type}
                  </th>
                  <td className="py-4 pl-2 text-deep-navy/70 align-top">{row.rec}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function RealisticExpectationsSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="realistic-expectations-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">What to Expect</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">What to Expect from Scar Management</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Complete scar elimination is not achievable. A scar represents a permanent change in tissue architecture that cannot be fully reversed to normal skin by any treatment currently available. What is achievable is significant reduction in scar visibility, improvement in texture and color, flattening of raised scars, filling of depressed scars, relief of itching and tightness, and restoration of movement restricted by contractures. These are meaningful outcomes that make a real difference to appearance and quality of life.
            </p>
            <p>
              The degree of improvement depends on the scar type, age, size, location, and the patient&apos;s skin type and healing characteristics. Fresh scars in the active phase of formation, typically the first 12 to 18 months after injury, respond more rapidly than fully mature scars that have been present for years. Older scars still respond to treatment but require more sessions and longer treatment courses to achieve similar degrees of improvement. Consistent adherence to the treatment plan, particularly the non-surgical home care components including silicone therapy and sun protection, is as important as the clinic-based procedures in determining the final outcome.
            </p>
            <p>
              Treatment timelines for scar management are measured in months, not days. Most patients see initial improvement within the first 2 to 3 months but the full benefit of a comprehensive scar management plan develops over 12 to 18 months. Patients who assess results after one or two sessions and conclude that treatment is not working are evaluating an incomplete course. The expectation should be set at the beginning of treatment: the plan requires patience and consistency, and the final result is assessed at 12 months or beyond.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function GoodCandidateSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="good-candidate-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Am I a Candidate?</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8">Who Should Seek Scar Management Treatment?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Any patient with a scar causing cosmetic concern, symptoms including itching or tightness, functional restriction, or psychological distress is appropriate for scar management consultation. There is no minimum scar age and no minimum size. A small acne scar on the face that significantly affects a patient&apos;s confidence is as valid an indication for treatment as a large burn scar causing functional restriction. Earlier treatment of active scars, during the first 12 to 18 months of scar development, produces better outcomes than treating fully matured scars.
            </p>
            <p>
              Patients with keloids or a history of keloid formation should seek assessment before any elective skin procedure, as the procedure itself can trigger new keloid formation. Patients with acne-prone skin should have active acne controlled before intensive scar treatment, as new acne lesions will form new scars that undermine the scar treatment being applied to existing scars. Patients with darker skin types require specific treatment protocols to avoid post-inflammatory hyperpigmentation and should seek assessment from a practitioner with specific experience in treating darker skin.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  return (
    <section className="bg-[#FAFAF8] py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {SCAR_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`scar-mgmt-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Scar Management Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain assesses your scar type, age, location, and symptoms to design a treatment plan combining the most effective modalities for your specific scar. Realistic outcomes are discussed honestly, including what degree of improvement is achievable and what the treatment course involves over time. You leave with a clear, sequenced plan and a practical understanding of what each stage requires from you.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Keloid Treatment",
      description: "Dedicated management for keloid scars requiring combination steroid and surgical protocols.",
      href: KELOID_PATH,
    },
    {
      name: "Burns & Burn Scar Treatment",
      description: "Comprehensive management for burn injuries and burn scars.",
      href: BURNS_PATH,
    },
    {
      name: "Chemical Peels",
      description: "Surface skin renewal for pigmented scars and post-inflammatory hyperpigmentation.",
      href: CHEMICAL_PEELS_PATH,
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function ScarManagementPage() {
  return (
    <>
      <SEO
        title="Scar Management Treatment | Dr. Vicky Jain"
        description="Acne scars, surgical scars, burn scars, and keloids treated with advanced combination therapy. Dr. Vicky Jain provides expert scar management. Book today."
        canonical="/scar-management-treatment-mumbai"
      />
      <ScarManagementJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <EarlyExpectationsAlert />
      <DrJainApproachSection />
      <TypesOfScarsSection />
      <TreatmentOptionsSection />
      <TreatmentByScarTypeSection />
      <RealisticExpectationsSection />
      <GoodCandidateSection />
      <PatientTestimonials procedureName="Burn" />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
